import React from 'react';
import { css } from '@emotion/react';
import { IconButton, styled, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { ArrowRightAlt, HelpOutline, MoreHoriz } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { color } from '../../../../styles/color';
import { getPrintPx } from './printChartOptions';
import { useServiceTranslation } from '@/hooks/useServiceTranslation';

const FlexWrap = css`
  display: flex;
  flex-wrap: wrap;
`;

const CaptionContainer = (isPrinting: boolean) => css`
  display: grid;
  align-items: center;
  height: fit-content;
  grid-template-columns: minmax(${getPrintPx(128, isPrinting)}px, max-content) ${getPrintPx(40, isPrinting)}px repeat(7, ${getPrintPx(28, isPrinting)}px) minmax(${getPrintPx(28, isPrinting)}px, max-content) ${getPrintPx(28, isPrinting)}px;
  font-size: ${getPrintPx(12, isPrinting)}px;
  color: ${color.dark_gray};
`;

const CaptionTitle = (isPrinting: boolean) => css`
  padding-right: ${getPrintPx(8, isPrinting)}px;
`;

const LargeLabel = (isPrinting: boolean) => css`
  padding-right: ${getPrintPx(8, isPrinting)}px;
`;

const LabelContainer = (isPrinting: boolean) => css`
  display: flex;
  align-items: center;
  font-size: ${getPrintPx(12, isPrinting)}px;
  line-height: 100%;
  margin: ${getPrintPx(4, isPrinting)}px ${getPrintPx(24, isPrinting)}px ${getPrintPx(4, isPrinting)}px 0;
  color: ${color.dark_gray};
`;

const CaptionRow = css`
  display: flex;
  align-items: center;
`;

const BgColorGrid = (colorCode: string, isPrinting: boolean, opacity: number = 1) => css`
  display: flex;
  height: ${getPrintPx(24, isPrinting)}px;
  width: inherit;
  justify-content: center;
  align-items: center;
  background-color: ${`rgba(${parseInt(colorCode.slice(1, 3), 16)}, ${parseInt(colorCode.slice(3, 5), 16)}, ${parseInt(
    colorCode.slice(5, 7),
    16
  )}, ${opacity})`};
`;

const ColorShape = (shape: 'square' | 'circle', bgColor: string, isPrinting: boolean) => css`
  background: ${bgColor};
  border: 1px solid ${color.border_gray};
  border-radius: ${shape === 'circle' ? '6px' : 'none'};
  width: ${getPrintPx(12, isPrinting)}px;
  height: ${getPrintPx(12, isPrinting)}px;
`;

const LabelMargin = (isPrinting: boolean) => css`
  margin-left: ${getPrintPx(8, isPrinting)}px;
`;

const YellowLine = (isPrinting: boolean) => css`
  background: ${color.notification_yellow};
  width: ${getPrintPx(30, isPrinting)}px;
  height: ${getPrintPx(3, isPrinting)}px;
`;

const StyledPopper = css`
  font-size: 14px;
`;

const PostureLabel = (LabelColor: string, isPrinting: boolean) => css`
  position: relative;
  padding-left: ${getPrintPx(16, isPrinting)}px;
  line-height: ${getPrintPx(24, isPrinting)}px;
  border-radius: 8px;
  margin-right: ${getPrintPx(16, isPrinting)}px;

  ::before {
    position: absolute;
    content: '';
    display: inline-block;
    top: 50%;
    transform: translateY(-50%);
    left: 0;
    width: ${getPrintPx(8, isPrinting)}px;
    height: ${getPrintPx(24, isPrinting)}px;
    background-color: ${LabelColor};
  }
`;

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 380,
  },
});

export const HelpTooltip = ({ title, description }: { title: string; description: string }) => (
    <CustomWidthTooltip
        sx={{ padding: 0 }}
        title={
          <div css={StyledPopper}>
            <strong>{title}</strong>
            <div>{description}</div>
          </div>
        }
        placement='top'
    >
      <IconButton sx={{ padding: 0 }}>
        <HelpOutline />
      </IconButton>
    </CustomWidthTooltip>
);

type CaptionProps = {
  isPrinting?: boolean;
};

export const UrineTrendGraphCaption = React.memo(({ isPrinting = false }: CaptionProps) => {
  const { t } = useTranslation();

  return (
    <div>
      <div css={CaptionContainer(isPrinting)}>
        <span css={CaptionTitle(isPrinting)}>{t('UrineRetentionTendency', '尿のたまり具合傾向:')}:</span>
        <span>{t('Weak', '弱い')}</span>
        <span>
          <ArrowRightAlt fontSize={isPrinting ? 'small' : 'medium'} />
        </span>
        <span css={BgColorGrid('#CC4444', isPrinting, 0.1)}>0</span>
        <span css={BgColorGrid('#CC4444', isPrinting, 0.25)}>
          <MoreHoriz fontSize={isPrinting ? 'small' : 'medium'} />
        </span>
        <span css={BgColorGrid('#CC4444', isPrinting, 0.5)}>50</span>
        <span css={BgColorGrid('#CC4444', isPrinting, 0.75)}>
          <MoreHoriz fontSize={isPrinting ? 'small' : 'medium'} />
        </span>
        <span css={BgColorGrid('#CC4444', isPrinting, 1)}>100</span>
        <span>
          <ArrowRightAlt fontSize={isPrinting ? 'small' : 'medium'} />
        </span>
        <span css={LargeLabel(isPrinting)}>{t('Strong', '強い')}</span>
        {!isPrinting && (
          <HelpTooltip
            title={t(
              'TrendOfUrineRetentionDuringTheEntireCollectionPeriod',
              '集計期間全体における尿のたまり具合の傾向値'
            )}
            description={t(
              'ThePercentageOfCasesExceedingTheSetThresholdIsDisplayedTheDarkerTheRedTheStrongerTheTendencyForUrineToAccumulate',
              '※設定した閾値を超えた割合を表示しており、赤色が濃いほど尿のたまり具合の傾向が強い'
            )}
          />
        )}
        <span css={CaptionTitle(isPrinting)}>{t('AverageUrineAccumulation', '尿のたまり具合平均')}:</span>
        <span>{t('Less', '少ない')}</span>
        <span>
          <ArrowRightAlt fontSize={isPrinting ? 'small' : 'medium'} />
        </span>
        <span css={BgColorGrid('#00AA66', isPrinting, 0.1)}>0</span>
        <span css={BgColorGrid('#00AA66', isPrinting, 0.25)}>
          <MoreHoriz fontSize={isPrinting ? 'small' : 'medium'} />
        </span>
        <span css={BgColorGrid('#00AA66', isPrinting, 0.5)}>5</span>
        <span css={BgColorGrid('#00AA66', isPrinting, 0.75)}>
          <MoreHoriz fontSize={isPrinting ? 'small' : 'medium'} />
        </span>
        <span css={BgColorGrid('#00AA66', isPrinting, 1)}>10</span>
        <span>
          <ArrowRightAlt fontSize={isPrinting ? 'small' : 'medium'} />
        </span>
        <span css={LargeLabel(isPrinting)}>{t('More', '多い')}</span>
        {!isPrinting && (
          <HelpTooltip
            title={`${t(
              'AverageUrineRetentionDuringTheEntireCollectionPeriod',
              '集計期間全体における尿のたまり具合の平均値'
            )}${t('DFreeValueExpressedIn0To10Scale', '（DFree数値の0～10段階表記）')}`}
            description={t(
              'TheDarkerTheGreenTheHigherTheAverageLevelOfUrineAccumulation',
              '※緑色が濃いほど尿のたまり具合の平均値が高い'
            )}
          />
        )}
        <span css={CaptionTitle(isPrinting)}>{t('UrineAccumulation', '尿のたまり具合')}:</span>
        <span>{t('Less', '少ない')}</span>
        <span>
          <ArrowRightAlt fontSize={isPrinting ? 'small' : 'medium'} />
        </span>
        <span css={BgColorGrid('#00AAFF', isPrinting, 0.1)}>0</span>
        <span css={BgColorGrid('#00AAFF', isPrinting, 0.25)}>
          <MoreHoriz fontSize={isPrinting ? 'small' : 'medium'} />
        </span>
        <span css={BgColorGrid('#00AAFF', isPrinting, 0.5)}>5</span>
        <span css={BgColorGrid('#00AAFF', isPrinting, 0.75)}>
          <MoreHoriz fontSize={isPrinting ? 'small' : 'medium'} />
        </span>
        <span css={BgColorGrid('#00AAFF', isPrinting, 1)}>10</span>
        <span>
          <ArrowRightAlt fontSize={isPrinting ? 'small' : 'medium'} />
        </span>
        <span css={LargeLabel(isPrinting)}>{t('More', '多い')}</span>
        {!isPrinting && (
          <HelpTooltip
            title={`${t('AverageUrineRetentionDuringTheCollectionPeriod', '集計期間における尿のたまり具合の平均値')}${t(
              'AveragingTheDFreeValueOnAScaleOf0To10Every30Minutes',
              '（DFree数値の0～10段階表記を30分ごとに平均した値）'
            )}`}
            description={t(
              'TheDarkerTheBlueTheHigherTheAveragLevelOfUrineAccumulation',
              '※青色が濃いほど尿のたまり具合の平均値が高い'
            )}
          />
        )}
      </div>
    </div>
  );
});

export const ActivityStatusGraphCaption = React.memo(({ isPrinting = false }: CaptionProps) => {
  const { t } = useTranslation();
  const postureLabelArray = [
    { label: t('StandingAndSitting', '立位＆座位'), color: '#FFF4CC' },
    { label: t('SupinePosition', '仰臥位'), color: '#141F80' },
    { label: t('RightSupinePosition', '右側臥位'), color: '#4EC1D0' },
    { label: t('LeftSupinePosition', '左側臥位'), color: '#5855DB' },
    { label: t('Moving(WhileLyingInBed)', '動いている（寝）'), color: '#B092E2' },
    { label: t('Moving(Standing/Sitting)', '動いている（立・座）'), color: '#FF7152' },
    { label: t('PronePosition', 'うつ伏せ'), color: '#D7F5FF' },
    { label: t('UpsideDown', '上下逆'), color: '#000000' },
  ];

  return (
    <div css={[CaptionRow]}>
      <div css={LabelContainer(isPrinting)}>{t('posture', '姿勢')}:</div>
      <div css={[LabelContainer(isPrinting), FlexWrap]}>
        {postureLabelArray.map((v, i) => {
          return (
            <span css={PostureLabel(v.color, isPrinting)} key={i}>
              {v.label}
            </span>
          );
        })}
      </div>
    </div>
  );
});

export const PlaceCaption = React.memo(({ isPrinting = false }: CaptionProps) => {
  const { serviceT } = useServiceTranslation();
  return (
    <div css={[CaptionRow, FlexWrap]}>
      <div css={LabelContainer(isPrinting)}>
        <div css={ColorShape('square', '#72FBFD', isPrinting)}></div>
        <span css={LabelMargin(isPrinting)}>{serviceT('toiletYesDiaperOrPadNo')}</span>
      </div>
      <div css={LabelContainer(isPrinting)}>
        <div css={ColorShape('square', '#75F94C', isPrinting)}></div>
        <span css={LabelMargin(isPrinting)}>{serviceT('toiletYesDiaperOrPadYes')}</span>
      </div>
      <div css={LabelContainer(isPrinting)}>
        <div css={ColorShape('square', '#CCCCCC', isPrinting)}></div>
        <span css={LabelMargin(isPrinting)}>{serviceT('toiletNoDiaperOrPadYes')}</span>
      </div>
      <div css={LabelContainer(isPrinting)}>
        <div css={ColorShape('square', '#6D6D6D', isPrinting)}></div>
        <span css={LabelMargin(isPrinting)}>{serviceT('toiletNoDiaperOrPadNo')}</span>
      </div>
    </div>
  );
});

export const NotificationCaption = React.memo(({ isPrinting = false }: CaptionProps) => {
  const { t, serviceT } = useServiceTranslation();
  return (
    <div css={[CaptionRow, FlexWrap]}>
      <div css={LabelContainer(isPrinting)}>
        <div css={YellowLine(isPrinting)}></div>
        <span css={LabelMargin(isPrinting)}>{t('almostThreshold', 'そろそろライン')}</span>
      </div>
      <div css={LabelContainer(isPrinting)}>
        <div css={ColorShape('circle', color.notification_yellow, isPrinting)}></div>
        <span css={LabelMargin(isPrinting)}>{t('almostNotificiation', 'そろそろ通知')}</span>
      </div>
      <div css={LabelContainer(isPrinting)}>
        <div css={ColorShape('circle', color.notification_pink, isPrinting)}></div>
        <span css={LabelMargin(isPrinting)}>{t('checkNotificiation', 'でたかも通知')}</span>
      </div>
      <div css={LabelContainer(isPrinting)}>
        <div css={ColorShape('circle', color.notification_red, isPrinting)}></div>
        <span css={LabelMargin(isPrinting)}>
          {serviceT('gotUpNotificiation')}
        </span>
      </div>
    </div>
  );
});
